import { Injectable, signal } from '@angular/core';
import {
  AppLanguage,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES
} from './language.types';
import { TranslationKey, en } from './translations/en';
import { es } from './translations/es';

const dictionaries: Record<AppLanguage, Record<TranslationKey, string>> = {
  en: { ...en },
  es
};

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly langSignal = signal<AppLanguage>(this.resolveInitialLanguage());

  readonly lang = this.langSignal.asReadonly();

  constructor() {
    this.applyDocumentLang(this.langSignal());
  }

  setLanguage(lang: AppLanguage): void {
    if (!SUPPORTED_LANGUAGES.includes(lang)) {
      return;
    }

    this.langSignal.set(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    this.applyDocumentLang(lang);
  }

  t(key: TranslationKey, params?: Record<string, string | number>): string {
    const dict = dictionaries[this.langSignal()];
    let text = dict[key] ?? dictionaries.en[key] ?? key;

    if (params) {
      for (const [name, value] of Object.entries(params)) {
        text = text.replaceAll(`{${name}}`, String(value));
      }
    }

    return text;
  }

  private resolveInitialLanguage(): AppLanguage {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved === 'en' || saved === 'es') {
      return saved;
    }

    return this.fromBrowserLanguage();
  }

  private fromBrowserLanguage(): AppLanguage {
    const candidates = [
      ...(navigator.languages ?? []),
      navigator.language
    ].filter(Boolean);

    for (const raw of candidates) {
      const code = raw.toLowerCase().split('-')[0];
      if (code === 'es') {
        return 'es';
      }
      if (code === 'en') {
        return 'en';
      }
    }

    return DEFAULT_LANGUAGE;
  }

  private applyDocumentLang(lang: AppLanguage): void {
    document.documentElement.lang = lang;
  }
}
